// src/screens/Pipeline.jsx
import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { getPipeline } from "../api/ingestMonitor";
import { THEME, appViewportStyle, screenStyle, btnStyle, panelStyle } from "../ui/theme";
import { Section, Pill, EmptyState } from "../ui/primitives";

import PipelineFunnel from "../layout/components/pipeline/PipelineFunnel";

/* =========================
   helpers
========================= */

function fmtInt(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString("pt-BR");
}

function fmtDateTimeBR(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

/* =========================
   scroll helpers
========================= */

function getScrollParent(el) {
  if (!el) return window;
  let p = el.parentElement;
  while (p) {
    const st = window.getComputedStyle(p);
    const oy = st.overflowY;
    const canScrollY = (oy === "auto" || oy === "scroll") && p.scrollHeight > p.clientHeight;
    if (canScrollY) return p;
    p = p.parentElement;
  }
  return window;
}

function smoothScrollToTarget(el, offsetPx) {
  if (!el) return;

  const scroller = getScrollParent(el);

  // window scroll (fallback)
  if (scroller === window) {
    const y = el.getBoundingClientRect().top + window.scrollY - offsetPx;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    return;
  }

  // container scroll (ex.: .content)
  const r = el.getBoundingClientRect();
  const cr = scroller.getBoundingClientRect();
  const y = scroller.scrollTop + (r.top - cr.top) - offsetPx;

  scroller.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
}

/* =========================
   media query
========================= */

function useMediaQuery(query) {
  const [ok, setOk] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  React.useEffect(() => {
    const m = window.matchMedia(query);
    const onChange = () => setOk(m.matches);
    onChange();
    m.addEventListener?.("change", onChange);
    return () => m.removeEventListener?.("change", onChange);
  }, [query]);

  return ok;
}

/* =========================
   compact table (desktop)
========================= */

function Table({ columns, rows, empty }) {
  return (
    <div style={panelStyle({ padding: 0, overflow: "hidden", maxWidth: "100%", minWidth: 0 })}>
      <div style={{ overflowX: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" }}>
        <table
          style={{
            width: "max-content",
            minWidth: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  style={{
                    textAlign: c.align || "left",
                    fontSize: 12,
                    color: THEME.muted,
                    fontWeight: 800,
                    padding: "10px 12px",
                    borderBottom: "1px solid rgba(255,255,255,.08)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {!rows?.length ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: 14 }}>
                  {empty || <EmptyState>Sem itens.</EmptyState>}
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={r?.sn || r?.hardware_id || idx}>
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      style={{
                        padding: "10px 12px",
                        borderBottom: "1px solid rgba(255,255,255,.06)",
                        fontSize: 12,
                        color: THEME.text,
                        whiteSpace: "nowrap",
                        textAlign: c.align || "left",
                      }}
                    >
                      {c.render ? c.render(r) : r?.[c.key] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =========================
   mobile row cards (expand)
========================= */

function LabelRow({ label, value }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 10, alignItems: "baseline" }}>
      <div style={{ fontSize: 12, color: THEME.muted, fontWeight: 800, whiteSpace: "nowrap" }}>
        {label}
      </div>
      <div style={{ fontSize: 12, color: THEME.text, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function RowCard({ title, subtitle, right, details }) {
  return (
    <details
      style={{
        borderRadius: 14,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,.10)",
        background: "rgba(255,255,255,.04)",
      }}
    >
      <summary
        style={{
          listStyle: "none",
          cursor: "pointer",
          padding: 0,
          outline: "none",
        }}
      >
        <div style={panelStyle({ padding: 12, borderRadius: 14 })}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 10,
              minWidth: 0,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 950,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
              </div>
              {subtitle ? (
                <div
                  style={{
                    marginTop: 2,
                    fontSize: 12,
                    color: THEME.muted,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {subtitle}
                </div>
              ) : null}
            </div>

            {right ? <div style={{ flex: "0 0 auto" }}>{right}</div> : null}
          </div>

          <div style={{ marginTop: 8, fontSize: 12, color: THEME.muted }}>
            Toque para ver detalhes ▾
          </div>
        </div>
      </summary>

      <div style={{ padding: "0 12px 12px 12px" }}>
        <div style={{ height: 1, background: "rgba(255,255,255,.08)", margin: "0 0 10px 0" }} />
        <div style={{ display: "grid", gap: 10 }}>{details}</div>
      </div>
    </details>
  );
}

function TableOrCards({ isMobile, columns, rows, empty, renderCard }) {
  if (!rows?.length) return empty || <EmptyState>Sem itens.</EmptyState>;
  if (!isMobile) return <Table columns={columns} rows={rows} empty={empty} />;
  return <div style={{ display: "grid", gap: 10 }}>{rows.map(renderCard)}</div>;
}

/* =========================
   Compact filter bar (global)
========================= */

function CompactFilterBar({ qText, setQText, onRefetch, isFetching }) {
  return (
    <div style={{ display: "grid", gap: 8, marginTop: 8, minWidth: 0 }}>
      <input
        value={qText}
        onChange={(e) => setQText(e.target.value)}
        placeholder="Filtro global (server): SN / flight_control_sn / machine_sn / hardware_id…"
        style={{
          width: "100%",
          height: 36,
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,.10)",
          background: "rgba(255,255,255,.04)",
          color: THEME.text,
          padding: "0 10px",
          outline: "none",
          fontSize: 12,
          minWidth: 0,
        }}
      />

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={() => setQText("")} style={btnStyle(true)} title="Limpar filtro global">
          Limpar
        </button>
        
      </div>
    </div>
  );
}

/* =========================
   Local search per list (client only)
========================= */

function MiniSearch({ value, onChange, placeholder = "Buscar nesta lista…" }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        height: 36,
        width: "100%",
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,.10)",
        background: "rgba(255,255,255,.035)",
        color: THEME.text,
        padding: "0 10px",
        outline: "none",
        fontSize: 12,
        minWidth: 0,
      }}
    />
  );
}

function norm(s) {
  return String(s || "").toLowerCase();
}

function rowMatches(r, term, keys) {
  const t = norm(term).trim();
  if (!t) return true;
  return (Array.isArray(keys) ? keys : []).some((k) => norm(r?.[k]).includes(t));
}

function SearchRow({ value, onChange, onClear, showing, total }) {
  const has = String(value || "").trim().length > 0;
  return (
    <div style={{ display: "grid", gap: 8, marginBottom: 8, minWidth: 0 }}>
      <MiniSearch value={value} onChange={onChange} />
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <button onClick={onClear} style={btnStyle(true)} disabled={!has}>
          Limpar
        </button>

        {has && (
          <Pill tone="muted">
            {fmtInt(showing)} / {fmtInt(total)}
          </Pill>
        )}
      </div>
    </div>
  );
}

/* =========================
   Pipeline Screen
========================= */

export default function Pipeline() {
  const isMobile = useMediaQuery("(max-width: 720px)");

  const [qText, setQText] = React.useState("");

  // local searches (client-only, do NOT affect funnel counts)
  const [s2, setS2] = React.useState("");
  const [s3, setS3] = React.useState("");
  const [s4, setS4] = React.useState("");
  const [s5, setS5] = React.useState("");
  const [s6, setS6] = React.useState("");

  // anchors for funnel jumps
  const refL2 = React.useRef(null);
  const refL3 = React.useRef(null);
  const refL4 = React.useRef(null);
  const refL5 = React.useRef(null);
  const refL6 = React.useRef(null);

  // TopBar fixa do seu TopBar.jsx: BAR_H = 52 (+ respiro)
  const TOPBAR_OFFSET_PX = 52 + 12;

  // ✅ scroll correto no container que realmente rola (ex.: .content)
  const jumpTo = React.useCallback((level) => {
    const map = { l2: refL2, l3: refL3, l4: refL4, l5: refL5, l6: refL6 };
    const el = map?.[level]?.current;
    if (!el) return;
    smoothScrollToTarget(el, TOPBAR_OFFSET_PX);
  }, []);

  const params = useMemo(
    () => ({
      q: qText.trim() || "",
      limit: 200,
    }),
    [qText]
  );

  const qPipe = useQuery({
    queryKey: ["pipeline", params],
    queryFn: () => getPipeline(params),
    staleTime: 30_000,
    keepPreviousData: true,
  });

  const data = qPipe.data;
  const levels = data?.levels || {};

  const l1 = levels?.l1_activated || { count: 0, rows: [] };
  const l2 = levels?.l2_activated_not_connected || { count: 0, rows: [] };
  const l3 = levels?.l3_seen_recent_6m || { count: 0, rows: [] };
  const l4 = levels?.l4_seen_offline_6m || { count: 0, rows: [] };
  const l5 = levels?.l5_seen_not_in_registry || { count: 0, rows: [] };
  const l6 = levels?.l6_seen_not_activated || { count: 0, rows: [] };

  // client-only filtering for views
  const l2RowsView = useMemo(
    () =>
      (Array.isArray(l2.rows) ? l2.rows : []).filter((r) =>
        rowMatches(r, s2, ["sn", "flight_control_sn", "machine_sn", "product_name"])
      ),
    [l2.rows, s2]
  );

  const l3RowsView = useMemo(
    () =>
      (Array.isArray(l3.rows) ? l3.rows : []).filter((r) =>
        rowMatches(r, s3, ["sn", "flight_control_sn", "product_name"])
      ),
    [l3.rows, s3]
  );

  const l4RowsView = useMemo(
    () =>
      (Array.isArray(l4.rows) ? l4.rows : []).filter((r) =>
        rowMatches(r, s4, ["sn", "flight_control_sn", "product_name"])
      ),
    [l4.rows, s4]
  );

  const l5RowsView = useMemo(
    () =>
      (Array.isArray(l5.rows) ? l5.rows : []).filter((r) => rowMatches(r, s5, ["hardware_id"])),
    [l5.rows, s5]
  );

  const l6RowsView = useMemo(
    () =>
      (Array.isArray(l6.rows) ? l6.rows : []).filter((r) =>
        rowMatches(r, s6, ["sn", "flight_control_sn", "machine_sn", "product_name"])
      ),
    [l6.rows, s6]
  );


  return (
    <div style={appViewportStyle()}>
      <div style={screenStyle()}>
        <div style={{ overflowX: "hidden" }}>
          <div
            style={{
              maxWidth: 980,
              margin: "0 auto",
              display: "grid",
              gap: 12,
              minWidth: 0,
              padding: "0 12px", // ✅ gutter mobile-first (alinha com o feeling das outras telas)
              boxSizing: "border-box",
            }}
          >
            {/* HEADER */}
            <Section
              style={{ minWidth: 0 }}
              title={
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
                  <b style={{ fontSize: 14, letterSpacing: 0.4 }}>DATA FLOW • PIPELINE</b>
                  <Pill tone="muted">6 níveis</Pill>
                </div>
              }
              subtitle="Ativação → conectividade → status (6m) → inventário"
            >
              {qPipe.isError && (
                <div
                  style={{
                    marginTop: 2,
                    padding: 10,
                    borderRadius: 14,
                    border: "1px solid rgba(251,113,133,0.28)",
                    background: "rgba(251,113,133,0.12)",
                    color: THEME.text,
                    fontSize: 12,
                  }}
                >
                  <b style={{ color: THEME.red }}>Erro:</b>{" "}
                  {qPipe.error?.message || "Falha ao carregar"}
                </div>
              )}

              <CompactFilterBar
                qText={qText}
                setQText={setQText}
                onRefetch={() => qPipe.refetch()}
                isFetching={qPipe.isFetching}
              />
            </Section>

            {/* LOADING / EMPTY */}
            {qPipe.isLoading && !data ? (
              <Section style={{ minWidth: 0 }}>
                <div style={{ color: THEME.muted, padding: 12 }}>Carregando pipeline…</div>
              </Section>
            ) : !data ? (
              <Section style={{ minWidth: 0 }}>
                <EmptyState>Sem dados para mostrar.</EmptyState>
              </Section>
            ) : (
              <>
                {/* FUNNEL (card único) */}
                <div style={{ minWidth: 0 }}>
                  <PipelineFunnel
                    l1={l1.count}
                    l2={l2.count}
                    l3={l3.count}
                    l4={l4.count}
                    l5={l5.count}
                    l6={l6.count}
                    onJump={jumpTo}
                    // dica: no funil, passe minVisualPct baixo se quiser
                    minVisualPct={2}
                  />
                </div>

                {/* N2 */}
                <div ref={refL2} style={{ scrollMarginTop: TOPBAR_OFFSET_PX }} />
                <Section
                  style={{ minWidth: 0 }}
                  title={
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
                      <b>N2 • Não conectaram</b>
                      <Pill tone={l2.count > 0 ? "warn" : "ok"}>{fmtInt(l2.count)}</Pill>
                    </div>
                  }
                  subtitle="Ativados sem flight_info. Search local não altera o funil."
                >
                  <SearchRow
                    value={s2}
                    onChange={setS2}
                    onClear={() => setS2("")}
                    showing={l2RowsView.length}
                    total={(l2.rows || []).length}
                  />

                  <TableOrCards
                    isMobile={isMobile}
                    columns={[
                      { key: "sn", label: "SN" },
                      { key: "product_name", label: "Produto" },
                      { key: "flight_control_sn", label: "Ctrl SN" },
                      { key: "machine_sn", label: "Machine SN" },
                      { key: "activated_at", label: "Ativado", render: (r) => fmtDateTimeBR(r?.activated_at) },
                    ]}
                    rows={l2RowsView}
                    empty={<EmptyState>Nenhum item corresponde ao search.</EmptyState>}
                    renderCard={(r, idx) => (
                      <RowCard
                        key={r?.sn || idx}
                        title={r?.sn || "—"}
                        subtitle={r?.product_name || "—"}
                        right={<Pill tone="warn">N2</Pill>}
                        details={
                          <>
                            <LabelRow label="Flight Ctrl SN" value={r?.flight_control_sn ?? "—"} />
                            <LabelRow label="Machine SN" value={r?.machine_sn ?? "—"} />
                            <LabelRow label="Ativado em" value={fmtDateTimeBR(r?.activated_at)} />
                          </>
                        }
                      />
                    )}
                  />
                </Section>

                {/* N3 */}
                <div ref={refL3} style={{ scrollMarginTop: TOPBAR_OFFSET_PX }} />
                <Section
                  style={{ minWidth: 0 }}
                  title={
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
                      <b>N3 • Ativos ≤ 6m</b>
                      <Pill tone="ok">{fmtInt(l3.count)}</Pill>
                    </div>
                  }
                  subtitle="last_seen_at nos últimos 6 meses. Search local não altera o funil."
                >
                  <SearchRow
                    value={s3}
                    onChange={setS3}
                    onClear={() => setS3("")}
                    showing={l3RowsView.length}
                    total={(l3.rows || []).length}
                  />

                  <TableOrCards
                    isMobile={isMobile}
                    columns={[
                      { key: "sn", label: "SN" },
                      { key: "product_name", label: "Produto" },
                      { key: "flight_control_sn", label: "Ctrl SN" },
                      { key: "last_seen_at", label: "Último", render: (r) => fmtDateTimeBR(r?.last_seen_at) },
                      { key: "flights_count", label: "Voos", align: "right" },
                    ]}
                    rows={l3RowsView}
                    empty={<EmptyState>Nenhum item corresponde ao search.</EmptyState>}
                    renderCard={(r, idx) => (
                      <RowCard
                        key={r?.sn || idx}
                        title={r?.sn || "—"}
                        subtitle={r?.product_name || "—"}
                        right={<Pill tone="ok">{fmtInt(r?.flights_count)}</Pill>}
                        details={
                          <>
                            <LabelRow label="Flight Ctrl SN" value={r?.flight_control_sn ?? "—"} />
                            <LabelRow label="Último dado" value={fmtDateTimeBR(r?.last_seen_at)} />
                            <LabelRow label="Voos" value={fmtInt(r?.flights_count)} />
                          </>
                        }
                      />
                    )}
                  />
                </Section>

                {/* N4 */}
                <div ref={refL4} style={{ scrollMarginTop: TOPBAR_OFFSET_PX }} />
                <Section
                  style={{ minWidth: 0 }}
                  title={
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
                      <b>N4 • Offline &gt; 6m</b>
                      <Pill tone={l4.count > 0 ? "alert" : "ok"}>{fmtInt(l4.count)}</Pill>
                    </div>
                  }
                  subtitle="Já enviaram, mas pararam há &gt; 6 meses. Search local não altera o funil."
                >
                  <SearchRow
                    value={s4}
                    onChange={setS4}
                    onClear={() => setS4("")}
                    showing={l4RowsView.length}
                    total={(l4.rows || []).length}
                  />

                  <TableOrCards
                    isMobile={isMobile}
                    columns={[
                      { key: "sn", label: "SN" },
                      { key: "product_name", label: "Produto" },
                      { key: "flight_control_sn", label: "Ctrl SN" },
                      { key: "last_seen_at", label: "Último", render: (r) => fmtDateTimeBR(r?.last_seen_at) },
                      { key: "flights_count", label: "Voos", align: "right" },
                    ]}
                    rows={l4RowsView}
                    empty={<EmptyState>Nenhum item corresponde ao search.</EmptyState>}
                    renderCard={(r, idx) => (
                      <RowCard
                        key={r?.sn || idx}
                        title={r?.sn || "—"}
                        subtitle={r?.product_name || "—"}
                        right={<Pill tone="alert">{fmtInt(r?.flights_count)}</Pill>}
                        details={
                          <>
                            <LabelRow label="Flight Ctrl SN" value={r?.flight_control_sn ?? "—"} />
                            <LabelRow label="Último dado" value={fmtDateTimeBR(r?.last_seen_at)} />
                            <LabelRow label="Voos" value={fmtInt(r?.flights_count)} />
                          </>
                        }
                      />
                    )}
                  />
                </Section>

                {/* N5 */}
                <div ref={refL5} style={{ scrollMarginTop: TOPBAR_OFFSET_PX }} />
                <Section
                  style={{ minWidth: 0 }}
                  title={
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
                      <b>N5 • Fora do registry</b>
                      <Pill tone={l5.count > 0 ? "alert" : "ok"}>{fmtInt(l5.count)}</Pill>
                    </div>
                  }
                  subtitle="hardware_id no flight_info sem match no registry. Search local não altera o funil."
                >
                  <SearchRow
                    value={s5}
                    onChange={setS5}
                    onClear={() => setS5("")}
                    showing={l5RowsView.length}
                    total={(l5.rows || []).length}
                  />

                  <TableOrCards
                    isMobile={isMobile}
                    columns={[
                      { key: "hardware_id", label: "Hardware ID" },
                      { key: "first_seen_at", label: "Primeiro", render: (r) => fmtDateTimeBR(r?.first_seen_at) },
                      { key: "last_seen_at", label: "Último", render: (r) => fmtDateTimeBR(r?.last_seen_at) },
                      { key: "flights_count", label: "Voos", align: "right" },
                    ]}
                    rows={l5RowsView}
                    empty={<EmptyState>Nenhum item corresponde ao search.</EmptyState>}
                    renderCard={(r, idx) => (
                      <RowCard
                        key={r?.hardware_id || idx}
                        title={r?.hardware_id || "—"}
                        subtitle={`Voos: ${fmtInt(r?.flights_count)}`}
                        right={<Pill tone="alert">N5</Pill>}
                        details={
                          <>
                            <LabelRow label="Primeiro dado" value={fmtDateTimeBR(r?.first_seen_at)} />
                            <LabelRow label="Último dado" value={fmtDateTimeBR(r?.last_seen_at)} />
                            <LabelRow label="Voos" value={fmtInt(r?.flights_count)} />
                          </>
                        }
                      />
                    )}
                  />
                </Section>

                {/* N6 */}
                <div ref={refL6} style={{ scrollMarginTop: TOPBAR_OFFSET_PX }} />
                <Section
                  style={{ minWidth: 0 }}
                  title={
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
                      <b>N6 • Sem ativação</b>
                      <Pill tone={l6.count > 0 ? "warn" : "ok"}>{fmtInt(l6.count)}</Pill>
                    </div>
                  }
                  subtitle="No registry, mas sem aircraft_activation. Search local não altera o funil."
                >
                  <SearchRow
                    value={s6}
                    onChange={setS6}
                    onClear={() => setS6("")}
                    showing={l6RowsView.length}
                    total={(l6.rows || []).length}
                  />

                  <TableOrCards
                    isMobile={isMobile}
                    columns={[
                      { key: "sn", label: "SN" },
                      { key: "product_name", label: "Produto" },
                      { key: "flight_control_sn", label: "Ctrl SN" },
                      { key: "machine_sn", label: "Machine SN" },
                      { key: "last_seen_at", label: "Último", render: (r) => fmtDateTimeBR(r?.last_seen_at) },
                      { key: "flights_count", label: "Voos", align: "right" },
                    ]}
                    rows={l6RowsView}
                    empty={<EmptyState>Nenhum item corresponde ao search.</EmptyState>}
                    renderCard={(r, idx) => (
                      <RowCard
                        key={r?.sn || idx}
                        title={r?.sn || "—"}
                        subtitle={r?.product_name || "—"}
                        right={<Pill tone="warn">{fmtInt(r?.flights_count)}</Pill>}
                        details={
                          <>
                            <LabelRow label="Flight Ctrl SN" value={r?.flight_control_sn ?? "—"} />
                            <LabelRow label="Machine SN" value={r?.machine_sn ?? "—"} />
                            <LabelRow label="Último dado" value={fmtDateTimeBR(r?.last_seen_at)} />
                            <LabelRow label="Voos" value={fmtInt(r?.flights_count)} />
                          </>
                        }
                      />
                    )}
                  />
                </Section>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

